// useProjectItems — the author's projects as list items, for every surface
// that lists them (the left menu, the Projects page): one projection, so the
// two never drift in name, meta line, actions or which one is open.
//
// A project (Studio v1 D1) comes from the state read (`Library.projects`).
// Opening one records the open (for "Last opened") and opens the Studio on
// it where the author was in it (projectPlaces.ts; at its start the first
// time). The open project (`frame.studioProject`, the one the author is in) is
// current in every view, not only while its Studio is shown: it stays open
// in the background, also across a reload (viewState.ts), until another one
// is opened (pen "Projects and Studio, two menu items", Decided 30 Oct).
// Rename and archive are the project's own commands; after one lands, the
// Library is read again (a refusal shows as a notice). "New project" opens
// the New project box (NewProjectHost). Pins, sort and density are the
// shared "projects" list view (useRecentList).

export { restoreProjectNow, useProjectItems };

import { Result } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type Dispatch, useState } from "react";
import {
    type ProjectSummary,
    projectsOf,
} from "../../../components/Projects/projects.js";
import type {
    RecentListCommand,
    RecentListView,
} from "../../../components/Sidebar/recentList.js";
import type { RecentItem } from "../../../components/Sidebar/sidebarEntries.js";
import {
    type DeskState,
    emptyDesk,
} from "../../../components/Studio/studioDesk.js";
import type { RecentThread } from "../../../components/WorkspaceLanding/WorkspaceLanding.js";
import type { FrameTransition } from "../../frame.js";
import type { AppPorts, Library, PortResult } from "../../ports.js";
import { projectPlaceOf, useProjectPlaces } from "../../projectsPreference.js";
import { recentListStore } from "../../recentListPreference.js";
import { conversationToOpenIn } from "../../studioConversation.js";
import { useAppShell } from "../context.js";
import { useRecentList } from "./useRecentList.js";

// Opening a project, from every surface that offers it (the left menu, All
// projects, ⌘K): record the open (for "Last opened"), then the Studio on it
// where the author was in it (at its start the first time), its chat on the
// project's most recently active conversation or a new one (#404: a
// conversation never carries across projects). `current`: the project the
// author is in, whose Studio already shows its place and conversation.
// `at`: a page to open on instead of the place it was left at (Search
// opens a material in its project, pen Q82).
const openProject = (
    deps: {
        readonly current: string | null;
        readonly showPlace: (desk: DeskState) => void;
        readonly dispatch: Dispatch<FrameTransition>;
        readonly conversations: readonly RecentThread[];
        readonly mkConversationId: () => string;
    },
    projectId: string,
    at?: DeskState,
): void => {
    recentListStore("projects").dispatch({ type: "opened", id: projectId });
    if (at !== undefined) deps.showPlace(at);
    if (projectId !== deps.current) {
        if (at === undefined)
            deps.showPlace(projectPlaceOf(projectId) ?? emptyDesk);
        const next = conversationToOpenIn(
            deps.conversations,
            projectId,
            deps.mkConversationId(),
        );
        deps.dispatch(
            next.nascent
                ? { type: "new-tab", conversationId: next.conversationId }
                : {
                      type: "enter-full-chat",
                      conversationId: next.conversationId,
                  },
        );
    }
    deps.dispatch({ type: "open-studio", projectId });
};

type ProjectItems = {
    // The active projects, and the archived ones (Restore, Delete …).
    readonly items: readonly RecentItem[];
    readonly archived: readonly RecentItem[];
    // "Delete …" asks first (RemoveProjectDialog's props).
    readonly removal: {
        readonly project: {
            readonly title: string;
            readonly archived: boolean;
        } | null;
        readonly onRemove: () => void;
        readonly onArchive: () => void;
        readonly onCancel: () => void;
    };
    // What each project holds and where the author was in it, by id
    // (projects.ts).
    readonly summaries: ReadonlyMap<string, ProjectSummary>;
    readonly view: RecentListView;
    readonly onCommand: (cmd: RecentListCommand) => void;
    // Opens the New project box.
    readonly newProject: () => void;
    // Opens a project in the Editor (`openProject`), optionally at a page.
    readonly open: (projectId: string, at?: DeskState) => void;
};

const useProjectItems = (): ProjectItems => {
    const { t } = useLingui();
    const {
        frame,
        library,
        setLibrary,
        ports,
        dispatch,
        showNotice,
        displayText,
        studio,
        conversationSummaries,
        mkConversationId,
    } = useAppShell();
    const places = useProjectPlaces();
    const [view, onCommand] = useRecentList("projects");
    // Which project this surface is renaming in place: view-ephemeral and
    // per surface, so the field opens only where the rename was asked for.
    const [renamingId, setRenamingId] = useState<string | null>(null);
    // Which project this surface asks "Delete …?" about (view state).
    const [removingId, setRemovingId] = useState<string | null>(null);
    const current = frame.studioProject;

    const open = (projectId: string, at?: DeskState) =>
        openProject(
            {
                current,
                showPlace: studio.showPlace,
                dispatch,
                conversations: conversationSummaries,
                mkConversationId,
            },
            projectId,
            at,
        );

    // A project command, then the Library read again; a refusal is a notice.
    const run = async <A>(
        write: () => PortResult<A>,
    ): Promise<Result.Result<A, unknown>> => {
        const r = await write();
        if (Result.isErr(r)) {
            showNotice({
                type: "lifecycle-failed",
                message: displayText(r.error),
            });
            return r;
        }
        const lib = await ports.loadLibrary();
        if (Result.isOk(lib)) setLibrary(lib.value);
        return r;
    };

    // Set aside, with an Undo (the notice restores it).
    const archive = (projectId: string, title: string) => {
        run(() => ports.archiveProject(projectId)).then((r) => {
            if (Result.isOk(r))
                showNotice({ type: "project-archived", projectId, title });
        });
    };

    const all = projectsOf(library, places);
    const projects = all.filter((p) => !p.archived);
    const titleOf = (title: string) =>
        title === "" ? t`Untitled project` : title;
    const items = projects.map((p): RecentItem => {
        const modules = t`${plural(p.modules, {
            one: "# module",
            other: "# modules",
        })}`;
        const pages = t`${plural(p.pages, {
            one: "# page",
            other: "# pages",
        })}`;
        return {
            type: "recent-item",
            list: "projects",
            id: p.id,
            title: titleOf(p.title),
            // "2 modules · 7 pages"
            meta: `${modules} · ${pages}`,
            ...(p.changedAt === undefined ? {} : { changedAt: p.changedAt }),
            current: current === p.id,
            open: () => open(p.id),
            rename: () => setRenamingId(p.id),
            archive: () => archive(p.id, titleOf(p.title)),
            confirmDelete: () => setRemovingId(p.id),
            renaming:
                renamingId === p.id
                    ? {
                          onSubmit: (title) => {
                              setRenamingId(null);
                              run(() => ports.renameProject(p.id, title));
                          },
                          onCancel: () => setRenamingId(null),
                      }
                    : undefined,
        };
    });
    const summaries = new Map(all.map((p) => [p.id, p]));
    // "New project" opens the box (#213); nothing exists until its Create.
    const newProject = () =>
        dispatch({ type: "open-overlay", overlay: { type: "new-project" } });
    const archived = all
        .filter((p) => p.archived)
        .map(
            (p): RecentItem => ({
                type: "recent-item",
                list: "projects",
                id: p.id,
                title: titleOf(p.title),
                meta: t`Archived`,
                current: false,
                // An archived project is read-only: restore it to work on it.
                open: () => {
                    run(() => ports.restoreProject(p.id));
                },
                restore: () => {
                    run(() => ports.restoreProject(p.id));
                },
                confirmDelete: () => setRemovingId(p.id),
            }),
        );
    const removing = all.find((p) => p.id === removingId);
    const removal = {
        project:
            removing === undefined
                ? null
                : {
                      title: titleOf(removing.title),
                      archived: removing.archived,
                  },
        onRemove: () => {
            setRemovingId(null);
            if (removing === undefined) return;
            run(() => ports.removeProject(removing.id)).then((r) => {
                if (Result.isOk(r) && frame.studioProject === removing.id)
                    dispatch({ type: "open-projects" });
            });
        },
        onArchive: () => {
            setRemovingId(null);
            if (removing !== undefined)
                archive(removing.id, titleOf(removing.title));
        },
        onCancel: () => setRemovingId(null),
    };
    return {
        items,
        archived,
        removal,
        summaries,
        view,
        onCommand,
        newProject,
        open,
    };
};

// The Undo of "Archived …" (the shell's notice): the project back from the
// archive, then the Library read again so it shows at once.
const restoreProjectNow = async (
    ports: AppPorts,
    setLibrary: (library: Library) => void,
    projectId: string,
): PortResult<void> => {
    const r = await ports.restoreProject(projectId);
    const lib = await ports.loadLibrary();
    if (Result.isOk(lib)) setLibrary(lib.value);
    return r;
};
